import {
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
	type CSSProperties,
	type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";

type Props = {
	options: string[];
	onSelect: (code: string) => void;
	disabled?: boolean;
	isLoading?: boolean;
	placeholder?: string;
};

/** Searchable single-select for non-priority GEO codes behind "+ add GEO". */
export function GeoSearchSelect({
	options,
	onSelect,
	disabled = false,
	isLoading = false,
	placeholder = "+ add GEO",
}: Props) {
	const listId = useId();
	const rootRef = useRef<HTMLDivElement | null>(null);
	const listRef = useRef<HTMLUListElement | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [portalStyle, setPortalStyle] = useState<CSSProperties>({});
	const [highlight, setHighlight] = useState(0);

	const filtered = useMemo(() => {
		const q = query.trim().toUpperCase();
		if (!q) return options;
		return options.filter((code) => code.includes(q));
	}, [options, query]);

	useEffect(() => {
		setHighlight(0);
	}, [query, open]);

	useEffect(() => {
		if (!open || !rootRef.current) return;
		const rect = rootRef.current.getBoundingClientRect();
		const margin = 6;
		const maxHeight = 220;
		const spaceBelow = window.innerHeight - rect.bottom - margin;
		const spaceAbove = rect.top - margin;
		if (spaceBelow >= 120) {
			setPortalStyle({
				position: "fixed",
				top: rect.bottom + margin,
				left: rect.left,
				minWidth: Math.max(rect.width, 120),
				maxHeight: Math.min(maxHeight, spaceBelow),
				zIndex: 2100,
			});
		} else {
			setPortalStyle({
				position: "fixed",
				bottom: window.innerHeight - rect.top + margin,
				left: rect.left,
				minWidth: Math.max(rect.width, 120),
				maxHeight: Math.min(maxHeight, spaceAbove),
				zIndex: 2100,
			});
		}
	}, [open, filtered.length]);

	useEffect(() => {
		if (!open) return;
		const onDoc = (e: MouseEvent) => {
			const target = e.target as Node;
			if (rootRef.current?.contains(target)) return;
			if (listRef.current?.contains(target)) return;
			setOpen(false);
		};
		document.addEventListener("mousedown", onDoc);
		return () => document.removeEventListener("mousedown", onDoc);
	}, [open]);

	const pick = (code: string) => {
		onSelect(code);
		setQuery("");
		setOpen(false);
		inputRef.current?.blur();
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setOpen(true);
			setHighlight((h) =>
				filtered.length === 0 ? 0 : Math.min(h + 1, filtered.length - 1),
			);
			return;
		}
		if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlight((h) => Math.max(h - 1, 0));
			return;
		}
		if (e.key === "Enter") {
			e.preventDefault();
			const code = filtered[highlight];
			if (code) pick(code);
			return;
		}
		if (e.key === "Escape") {
			setOpen(false);
			setQuery("");
		}
	};

	const list =
		open && !disabled ? (
			<ul
				ref={listRef}
				id={listId}
				className='account-details-modal__geo-search-list'
				role='listbox'
				style={portalStyle}>
				{isLoading ? (
					<li className='account-details-modal__geo-search-empty' role='presentation'>
						Завантаження…
					</li>
				) : filtered.length === 0 ? (
					<li className='account-details-modal__geo-search-empty' role='presentation'>
						Нічого не знайдено
					</li>
				) : (
					filtered.map((code, index) => (
						<li
							key={code}
							role='option'
							aria-selected={index === highlight}
							className={`account-details-modal__geo-search-item${
								index === highlight
									? " account-details-modal__geo-search-item--active"
									: ""
							}`}
							onMouseEnter={() => setHighlight(index)}
							onMouseDown={(e) => {
								e.preventDefault();
								pick(code);
							}}>
							{code}
						</li>
					))
				)}
			</ul>
		) : null;

	return (
		<div
			ref={rootRef}
			className={`account-details-modal__geo-search${open ? " account-details-modal__geo-search--open" : ""}`}>
			<input
				ref={inputRef}
				className='account-details-modal__add-geo-input'
				value={query}
				disabled={disabled}
				placeholder={placeholder}
				aria-label='Додати GEO'
				aria-autocomplete='list'
				aria-expanded={open}
				aria-controls={listId}
				role='combobox'
				autoComplete='off'
				onChange={(e) => {
					setQuery(e.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase());
					setOpen(true);
				}}
				onFocus={() => setOpen(true)}
				onKeyDown={onKeyDown}
			/>
			{typeof document !== "undefined"
				? createPortal(list, document.body)
				: null}
		</div>
	);
}
